import React, { useMemo } from 'react';
import { useForm, useWatch, FieldError, UseFormReturn } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FlexyFormLabel } from '@europrocurement/flexy-components';
import { FormObject } from '@europrocurement/flexy-form/components/FlexyForm/FlexyFormTypes';
import SirenAutocomplete from '@europrocurement/l2d-modules/modules/PrescriberClient/components/AddPrescriberClient/SirenAutocomplete/SirenAutocomplete';
import { CompanyType } from '@europrocurement/l2d-modules/modules/PrescriberClient/hooks/useSirenSearch';
import { usePrescripteurForm, Step1Data } from '../../providers/PrescripteurFormContext';

type StepIdentificationProps = {
    onNext: () => void;
};

const ETRANGERE_ITEMS = [
    { label: 'Non', value: false },
    { label: 'Oui', value: true },
];

const PERSONNE_TYPE_ITEMS = [
    { label: 'Personne morale', value: 'morale' },
    { label: 'Personne physique', value: 'physique' },
];

const StepIdentification: React.FunctionComponent<StepIdentificationProps> = function ({ onNext }) {
    const { step1, setStep1, setRneData } = usePrescripteurForm();

    const methods = useForm({
        mode: 'onTouched',
        defaultValues: {
            isEtrangere: [step1?.isEtrangere ?? false],
            personneType: [step1?.personneType ?? 'morale'],
            denomination: step1?.denomination ?? '',
            tvaIntracommunautaire: step1?.tvaIntracommunautaire ?? '',
            siren: step1?.siren ?? '',
            siret: step1?.siret ?? '',
            nom: step1?.nom ?? '',
            prenom: step1?.prenom ?? '',
        },
    });

    // selectItems stocke la valeur dans un tableau — on extrait le premier élément
    const isEtrangerRaw = useWatch({ control: methods.control, name: 'isEtrangere' });
    const personneTypeRaw = useWatch({ control: methods.control, name: 'personneType' });

    const isEtrangere = Array.isArray(isEtrangerRaw) ? isEtrangerRaw[0] === true : false;
    const personneType =
        ((Array.isArray(personneTypeRaw) ? personneTypeRaw[0] : personneTypeRaw) as string) ??
        'morale';

    const formStructure = useMemo((): FormStructure[] => {
        const structure: FormStructure[] = [
            {
                type: 'selectItems',
                name: 'isEtrangere',
                inputlabel: 'Le prescripteur est-il une société étrangère ?',
                mode: 'singleLine',
                multiple: false,
                lg: 12,
                md: 12,
                sm: 12,
                rules: {},
                items: ETRANGERE_ITEMS,
            },
        ];

        if (!isEtrangere) {
            structure.push({
                type: 'selectItems',
                name: 'personneType',
                inputlabel: "S'agit-il d'une personne physique ou morale ?",
                mode: 'singleLine',
                multiple: false,
                lg: 12,
                md: 12,
                sm: 12,
                rules: {},
                items: PERSONNE_TYPE_ITEMS,
            });
        }

        if (isEtrangere || personneType === 'morale') {
            structure.push({
                type: 'text',
                name: 'denomination',
                inputlabel: 'Dénomination',
                fullWidth: true,
                lg: 12,
                md: 12,
                sm: 12,
                rules: { required: 'La dénomination est obligatoire' },
            });
        }

        if (isEtrangere) {
            structure.push({
                type: 'text',
                name: 'tvaIntracommunautaire',
                inputlabel: 'Numéro TVA intracommunautaire',
                fullWidth: true,
                lg: 12,
                md: 12,
                sm: 12,
            });
        }

        if (!isEtrangere && personneType === 'morale') {
            structure.push({
                type: 'customItem',
                name: 'siren',
                lg: 12,
                md: 12,
                sm: 12,
                renderField: (fc) => {
                    const { ref, name } = fc.register('siren', {
                        required: 'Le SIREN est obligatoire',
                    });
                    const sirenError = fc.formState.errors?.siren as FieldError | undefined;
                    const currentValue = (fc.getValue('siren') as string) ?? '';

                    return (
                        <Box>
                            <input
                                type="hidden"
                                ref={ref}
                                name={name}
                            />
                            <input
                                type="hidden"
                                {...fc.register('siret')}
                            />
                            <FlexyFormLabel required>SIREN</FlexyFormLabel>
                            <SirenAutocomplete
                                defaultValue={currentValue}
                                placeholder="Rechercher par SIREN ou SIRET..."
                                hasError={!!sirenError}
                                onSelectItem={(company: CompanyType) => {
                                    fc.setValue('siren', company.siren ?? '', {
                                        shouldValidate: true,
                                    });
                                    fc.setValue('siret', company.siret ?? '');
                                    if (!fc.getValue('denomination')) {
                                        fc.setValue('denomination', company.denomination ?? '');
                                    }
                                    setRneData(company);
                                }}
                            />
                            {sirenError && (
                                <Typography
                                    color="error"
                                    variant="caption"
                                    sx={{ mt: 0.5, display: 'block' }}
                                >
                                    {sirenError.message}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            });
        }

        if (!isEtrangere && personneType === 'physique') {
            structure.push(
                {
                    type: 'text',
                    name: 'nom',
                    inputlabel: 'Nom',
                    fullWidth: true,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    rules: { required: 'Le nom est obligatoire' },
                },
                {
                    type: 'text',
                    name: 'prenom',
                    inputlabel: 'Prénom',
                    fullWidth: true,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    rules: { required: 'Le prénom est obligatoire' },
                },
            );
        }

        return structure;
    }, [isEtrangere, personneType, setRneData]);

    const handleSubmit = (values: Record<string, unknown>) => {
        const isEtrangereVal = Array.isArray(values.isEtrangere)
            ? values.isEtrangere[0] === true
            : values.isEtrangere === true;
        const personneTypeVal = (
            Array.isArray(values.personneType) ? values.personneType[0] : values.personneType
        ) as Step1Data['personneType'];

        setStep1({
            isEtrangere: isEtrangereVal,
            personneType: personneTypeVal ?? 'morale',
            denomination: (values.denomination as string) ?? '',
            tvaIntracommunautaire: (values.tvaIntracommunautaire as string) ?? '',
            siren: (values.siren as string) ?? '',
            siret: (values.siret as string) ?? '',
            nom: (values.nom as string) ?? '',
            prenom: (values.prenom as string) ?? '',
        });
        onNext();
    };

    return (
        <Box>
            <FlexyForm
                formContext={methods as unknown as UseFormReturn<FormObject>}
                formStructure={formStructure}
                onSubmit={handleSubmit}
                submitButton={{ displayed: false }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={methods.handleSubmit(handleSubmit)}
                    sx={{ padding: '16px 24px' }}
                >
                    Suivant
                </Button>
            </Box>
        </Box>
    );
};

export default StepIdentification;
